'use client';

import { Calendar } from '@shared/components/ui/shadcn/calendar';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import { memo, useState } from 'react';
import { Button } from '../../button';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover';
import type { FilterStructure } from '../types';

interface DateRangePickerProps {
  filterOption: FilterStructure;
  value: string;
  setValue: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  dataTestId?: string;
  translations?: {
    from?: string;
    to?: string;
  };
}

/**
 * Componente para filtros de rango de fechas
 * Utiliza el Calendar de shadcn en modo range
 * Formato de valor: "YYYY-MM-DD to YYYY-MM-DD"
 */
const DateRangePicker = memo(
  ({
    filterOption,
    value,
    setValue,
    placeholder,
    disabled = false,
    dataTestId,
    translations = {},
  }: DateRangePickerProps) => {
    const [open, setOpen] = useState(false);
    const config = filterOption.dateConfig;

    // 🎯 Función helper para parsear fechas en formato 'YYYY-MM-DD' a zona horaria local
    // Helper function to parse dates in 'YYYY-MM-DD' format to local timezone
    const parseLocalDate = (dateString: string): Date => {
      // Parsear el string 'YYYY-MM-DD' y crear fecha en zona horaria local
      // Parse 'YYYY-MM-DD' string and create date in local timezone
      const [year, month, day] = dateString.split('-').map(Number);
      // month - 1 porque Date usa meses 0-indexados (0 = enero, 11 = diciembre)
      // month - 1 because Date uses 0-indexed months (0 = January, 11 = December)
      return new Date(year, month - 1, day);
    };

    // Parsear el valor de rango de fechas
    const parseValue = (val: string) => {
      if (!val || !val.includes(' to '))
        return { from: undefined, to: undefined };

      const [fromStr, toStr] = val.split(' to ');
      return {
        from: fromStr ? parseLocalDate(fromStr) : undefined,
        to: toStr ? parseLocalDate(toStr) : undefined,
      };
    };

    // Formatear el rango de fechas para mostrar
    const formatDateRange = (val: string) => {
      const { from, to } = parseValue(val);
      if (!from) return '';

      const fromFormatted = format(from, 'PPP', { locale: es });
      if (!to) return `${translations.from || 'Desde'}: ${fromFormatted}`;

      const toFormatted = format(to, 'PPP', { locale: es });
      return `${fromFormatted} - ${toFormatted}`;
    };

    const handleRangeSelect = (
      range: { from?: Date; to?: Date } | undefined
    ) => {
      if (!range?.from) {
        setValue('');
        return;
      }

      if (range.to) {
        const formatted = `${range.from.toLocaleDateString('en-CA')} to ${range.to.toLocaleDateString('en-CA')}`;
        setValue(formatted);
        setOpen(false);
      }
    };

    const currentRange = parseValue(value);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start text-left font-normal h-10 px-3 py-2 bg-card-bg border-card-border overflow-hidden"
            disabled={disabled}
            data-testid={dataTestId}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? (
              <span className="truncate whitespace-nowrap overflow-hidden">
                {formatDateRange(value)}
              </span>
            ) : (
              <span className="text-muted-foreground">
                {config?.placeholder || placeholder}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            numberOfMonths={1}
            selected={currentRange.from ? currentRange : undefined}
            onSelect={handleRangeSelect}
            fromDate={config?.minDate}
            toDate={config?.maxDate}
          />
        </PopoverContent>
      </Popover>
    );
  }
);

DateRangePicker.displayName = 'DateRangePicker';

export default DateRangePicker;
